/** Derive inbox rows only from authorized snapshot messages involving the current actor.
 * This defensive browser filter does not grant read/send permission; the server remains authority. */
export function directThreads(state, who, query = '') {
  const actor = state.me.id,
    blocked = new Set(state.settings.blocked),
    threads = new Map();
  const needle = query.trim().toLocaleLowerCase();
  for (const message of state.messages) {
    if (message.from !== actor && message.to !== actor) continue;
    const id = message.from === actor ? message.to : message.from;
    const person = who(id);
    if (id === actor || blocked.has(id) || !person) continue;
    if (!threads.has(id)) threads.set(id, { id, person, messages: [] });
    threads.get(id).messages.push(message);
  }
  return [...threads.values()]
    .map((thread) => {
      thread.messages.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
      const latest = thread.messages.at(-1);
      const identity = `${thread.person.name} ${thread.person.handle ?? ''}`.toLocaleLowerCase();
      const matched = needle
        ? thread.messages.findLast((message) => message.text.toLocaleLowerCase().includes(needle))
        : undefined;
      return {
        ...thread,
        latest,
        matched,
        matches: !needle || identity.includes(needle) || !!matched,
      };
    })
    .filter((thread) => thread.matches)
    .sort((a, b) => b.latest.createdAt - a.latest.createdAt || a.id.localeCompare(b.id));
}

/** Inbox, recipient picker and direct chat share actor-scoped drafts with the Circle owner.
 * Routes select a chat explicitly; opening the inbox never auto-selects or marks a message read. */
export function createDirectMessages(ctx, { bindDraft, drafts, messageRow }) {
  let actor,
    query = '',
    generation = 0;
  function syncActor() {
    if (actor !== ctx.state.me.id) {
      actor = ctx.state.me.id;
      query = '';
    }
  }
  const friends = () =>
    ctx.state.friends.filter((id) => !ctx.state.settings.blocked.includes(id) && ctx.who(id));
  const back = () => ctx.iconButton('arrow-left', 'Back to messages', () => ctx.go('messages'));
  function row(person, href, preview, time, focusKey) {
    const link = ctx.link('', href, 'direct-inbox-row');
    link.dataset.focusKey = focusKey;
    const copy = ctx.e('div', 'direct-inbox-copy');
    copy.append(ctx.e('strong', '', person.name));
    if (person.handle) copy.append(ctx.e('span', 'direct-inbox-handle', `@${person.handle}`));
    if (preview) copy.append(ctx.e('span', 'direct-inbox-preview', preview));
    link.append(ctx.avatar(person), copy);
    if (time !== undefined) link.append(ctx.e('small', 'direct-inbox-time', ctx.time(time)));
    link.append(ctx.icon('chevron-right'));
    return link;
  }
  function newMessage() {
    const content = ctx.e('div', 'form-stack');
    const search = ctx.field('input', {
      type: 'search',
      className: 'direct-inbox-search',
      ariaLabel: ctx.uiText('Search friends by name or handle'),
      placeholder: ctx.uiText('Search friends by name or handle'),
      maxLength: 120,
    });
    const results = ctx.e('div', 'direct-inbox-list');
    const empty = ctx.e('p', 'subtle');
    const more = ctx.button(
      'Load more',
      () => {
        limit += 30;
        paint();
      },
      'secondary-button',
    );
    let limit = 30;
    function paint() {
      const needle = search.value.trim().toLocaleLowerCase();
      const ids = friends().filter((id) =>
        `${ctx.who(id).name} ${ctx.who(id).handle ?? ''}`.toLocaleLowerCase().includes(needle),
      );
      results.replaceChildren();
      for (const id of ids.slice(0, limit)) {
        const choice = ctx.button(
          '',
          () => {
            ctx.modal.close();
            ctx.go(`messages/${id}`);
          },
          'direct-inbox-row',
        );
        const copy = ctx.e('div', 'direct-inbox-copy');
        copy.append(ctx.e('strong', '', ctx.who(id).name));
        if (ctx.who(id).handle)
          copy.append(ctx.e('span', 'direct-inbox-handle', `@${ctx.who(id).handle}`));
        choice.append(ctx.avatar(ctx.who(id)), copy);
        choice.dataset.focusKey = `new-message-${id}`;
        results.append(choice);
      }
      empty.textContent = ids.length
        ? ''
        : ctx.uiText(
            friends().length
              ? 'No friends match this search.'
              : 'Connect with a friend to start a conversation.',
          );
      more.hidden = ids.length <= limit;
    }
    search.value = '';
    search.addEventListener('input', () => {
      limit = 30;
      paint();
    });
    content.append(
      ctx.e('p', 'subtle', ctx.uiText('New conversations are available with friends.')),
      search,
      results,
      empty,
      more,
      ctx.link(ctx.uiText('Find people'), '#/discover', 'text-button'),
    );
    paint();
    ctx.dialog('New message', content);
    search.focus({ preventScroll: true });
  }
  function inbox() {
    ctx.view.append(
      ctx.append(
        ctx.e('header', 'direct-inbox-header'),
        ctx.e('h1', '', ctx.uiText('Messages')),
        ctx.iconButton('pencil', 'New message', newMessage),
      ),
    );
    const search = ctx.field('input', {
      type: 'search',
      className: 'direct-inbox-search',
      value: query,
      maxLength: 120,
      ariaLabel: ctx.uiText('Search conversations'),
      placeholder: ctx.uiText('Search names, handles or messages'),
    });
    const list = ctx.e('nav', 'direct-inbox-list');
    list.setAttribute('aria-label', ctx.uiText('Conversations'));
    const status = ctx.e('p', 'subtle');
    status.setAttribute('role', 'status');
    const more = ctx.button(
      'Load more',
      () => {
        limit += 30;
        paint();
      },
      'secondary-button',
    );
    let limit = 30;
    function paint() {
      const threads = directThreads(ctx.state, ctx.who, query);
      list.replaceChildren();
      for (const thread of threads.slice(0, limit)) {
        const preview = thread.matched ?? thread.latest;
        list.append(
          row(
            thread.person,
            `#/messages/${thread.id}`,
            `${preview.from === actor ? ctx.uiText('You:') + ' ' : ''}${preview.text}`,
            preview.createdAt,
            `direct-thread-${thread.id}`,
          ),
        );
      }
      status.textContent = threads.length
        ? ''
        : ctx.uiText(
            query.trim()
              ? 'No conversations match this search.'
              : 'No conversations yet. Start a new message.',
          );
      more.hidden = threads.length <= limit;
    }
    search.addEventListener('input', () => {
      query = search.value;
      limit = 30;
      paint();
    });
    ctx.view.append(search, list, status, more);
    paint();
  }
  function chat(id, token) {
    const person = ctx.who(id);
    const thread = directThreads(ctx.state, ctx.who).find((item) => item.id === id);
    if (
      !person ||
      ctx.state.settings.blocked.includes(id) ||
      (!thread && !friends().includes(id))
    ) {
      ctx.view.append(back(), ctx.empty('Conversation unavailable.'));
      return;
    }
    const conversation = ctx.e('section', 'conversation direct-conversation');
    const title = ctx.e('h1', '', person.name);
    title.tabIndex = -1;
    conversation.append(
      ctx.append(
        ctx.e('header', 'circle-header'),
        back(),
        ctx.append(
          ctx.e('div'),
          title,
          person.handle ? ctx.e('small', 'subtle', `@${person.handle}`) : null,
        ),
        ctx.iconButton('user', 'View profile', () => ctx.go(`profile/${id}`)),
      ),
    );
    const messages = ctx.e('div', 'message-list direct-transcript');
    messages.tabIndex = 0;
    messages.setAttribute('aria-label', ctx.uiText('Direct conversation'));
    let match;
    const needle = query.trim().toLocaleLowerCase();
    for (const message of thread?.messages ?? []) {
      const node = messageRow({ ...message, authorId: message.from });
      if (needle && message.text.toLocaleLowerCase().includes(needle)) {
        node.classList.add('message-search-match');
        node.tabIndex = -1;
        match = node;
      }
      messages.append(node);
    }
    if (!thread)
      messages.append(
        ctx.e('p', 'subtle', ctx.uiText('Say hello. Your messages stay in this local demo.')),
      );
    conversation.append(messages);
    if (friends().includes(id)) compose(id, conversation, messages, token);
    else
      conversation.append(
        ctx.e('p', 'subtle', ctx.uiText('Connect as friends to send new messages.')),
      );
    ctx.view.append(conversation);
    if (match) {
      match.scrollIntoView?.({ block: 'center' });
      match.focus({ preventScroll: true });
    } else title.focus({ preventScroll: true });
  }
  function compose(id, conversation, messages, token) {
    const form = ctx.e('form', 'message-form');
    const text = ctx.field('textarea', {
      placeholder: ctx.uiText('Write a message…'),
      required: true,
      maxLength: 2000,
      ariaLabel: ctx.uiText('Message text'),
    });
    const draftKey = bindDraft(text, `direct:${id}`);
    const send = ctx.e('button', 'primary-button');
    send.append(ctx.icon('send'));
    send.setAttribute('aria-label', ctx.uiText('Send message'));
    send.title = ctx.uiText('Send message');
    form.append(text, send);
    let pending = false;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending) return;
      pending = true;
      send.setAttribute('aria-busy', 'true');
      const sentText = text.value,
        sentActor = actor;
      const known = new Set(ctx.state.messages.map((message) => message.id));
      try {
        const snapshot = await ctx.command(
          { type: 'message', id, text: sentText },
          { paint: false },
        );
        if (ctx.state.me.id !== sentActor) return;
        if (drafts.get(draftKey) === sentText) drafts.delete(draftKey);
        if (generation !== token || conversation.isConnected === false) return;
        const added = (snapshot?.messages ?? ctx.state.messages).filter(
          (message) =>
            !known.has(message.id) &&
            ((message.from === sentActor && message.to === id) ||
              (message.to === sentActor && message.from === id)),
        );
        if (
          !directThreads(
            {
              ...ctx.state,
              messages: ctx.state.messages.filter((message) => known.has(message.id)),
            },
            ctx.who,
          ).some((thread) => thread.id === id)
        )
          messages.replaceChildren();
        for (const message of added) {
          const row = messageRow({ ...message, authorId: message.from });
          row.classList.add('message-enter');
          messages.append(row);
        }
        if (text.value === sentText) text.value = '';
        messages.scrollTop = messages.scrollHeight;
        text.focus();
      } catch (error) {
        if (ctx.state.me.id === sentActor && generation === token) ctx.toast(error.message);
      } finally {
        pending = false;
        send.removeAttribute('aria-busy');
      }
    });
    conversation.append(form);
  }
  return {
    renderMessages(id) {
      syncActor();
      const token = ++generation;
      if (id) chat(id, token);
      else inbox();
    },
  };
}
